import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  Button, 
  CircularProgress, 
  TextField, 
  Typography, 
  Paper,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import mymaLogo from '../../assets/images/myma-logo-full-color.svg';
import { primary, secondary } from '../../themes/colors';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Get the intended destination from location state, or default to home
  const from = location.state?.from?.pathname || '/';

  useEffect(() => {
    // If already authenticated, redirect to the home page
    if (isAuthenticated) {
      navigate('/', { replace: true });
      return;
    }
    
    // Check if username is stored
    const storedUsername = localStorage.getItem('username');
    const storedRememberMe = localStorage.getItem('rememberMe');
    if (storedUsername && storedRememberMe === 'true') {
      setUsername(storedUsername);
      setRememberMe(true);
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!username || !password) {
      setError(t('enter_credentials'));
      return;
    }

    try {
      setIsLoading(true);
      // Use the context login function instead of direct service call
      await login(username, password);
      
      if (rememberMe) {
        localStorage.setItem('username', username);
        localStorage.setItem('rememberMe', 'true');
      } else {
        localStorage.removeItem('username');
        localStorage.removeItem('rememberMe');
      }
      
      // Use replace: true to prevent navigation loops
      navigate(from, { replace: true });
    } catch (error) {
      setError(t('invalid_credentials'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'center',
        backgroundColor: primary.main,
        py: { xs: 2, sm: 0 },
      }}
    >
      <Paper
        elevation={4}
        sx={{
          width: '100%',
          maxWidth: 450,
          p: { xs: 3, sm: 4 },
          mx: { xs: 2, sm: 'auto' },
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          component="img"
          src={mymaLogo}
          alt="Myma Logo"
          sx={{
            height: 60,
            maxWidth: '100%',
            width: 'auto',
            mb: { xs: 3, sm: 4 }
          }}
        />
        
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500, color: primary.main, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          {t('welcome_back')}
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          {t('sign_in_account')}
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            label={t('email_address')}
            name="username"
            autoComplete="email"
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={isLoading}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label={t('password')}
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
          />
          
          <FormControlLabel
            control={
              <Checkbox 
                checked={rememberMe} 
                onChange={(e) => setRememberMe(e.target.checked)}
                color="primary" 
                disabled={isLoading}
              />
            }
            label={t('remember_me')}
            sx={{ mt: 1 }}
          />
          
          {error && (
            <Typography color="error" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isLoading}
            sx={{
              mt: 3,
              mb: 2,
              py: 1.5,
              backgroundColor: secondary.main,
              '&:hover': {
                backgroundColor: secondary.dark
              }
            }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : t('sign_in')}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;